import React, { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  DataGrid,
  Icon,
  IconButton,
  CellTypes,
} from '@fashionnovateam/nova-ui-toolkit';
import Roles, {
  UserRoleDisplay,
  Pages,
  PagePermissions,
  Actions,
  RolesType,
} from '../../Lib/Constants/roles';
import Header from '../Header';
import Page from '../Page';
import { FilterSearch } from '../FilterSearch/FilterSearch';

type RouteInfo = {
  rolePermissions: {
    [role: string]: {
      create: boolean;
      read: boolean;
      update: boolean;
      delete: boolean;
      special?: string[];
      feature_permission?: string;
    };
  };
};

type PermissionDisplayData = {
  [route: string]: RouteInfo;
};

function getFeaturePermission(page: string): string | undefined {
  switch (page) {
    case 'dev-tools':
      return 'dev_tools';
    case 'users/:id/printing-setup':
      return 'allow_printing';
    case 'users/:id/purchase-orders/:purchaseOrderNumber/asn':
      return 'allow_printing';
    default:
      return undefined;
  }
}

function extractRoutes(): string[] {
  const routes = new Set<string>();
  const permissionRoutes = Object.keys(PagePermissions);

  // Add routes from PagePermissions
  permissionRoutes.forEach((route) => {
    routes.add(route);
  });

  // Add public routes that don't require permissions
  const publicRoutes = [
    'login',
    'influencer-login',
    'register-basic',
    'password-reset',
    'influencer-password-reset',
    'influencer-register',
    'vendor-form',
    'maintenance',
  ];
  publicRoutes.forEach((route) => routes.add(route));

  return Array.from(routes).sort();
}

function formatPermissionsData(): PermissionDisplayData {
  const formattedData: PermissionDisplayData = {};

  const routerRoutes = extractRoutes();
  const permissionPaths = new Set(Object.keys(PagePermissions));

  routerRoutes.forEach((route) => {
    formattedData[route] = {
      rolePermissions: {},
    };

    // If route has permissions defined, process them
    if (permissionPaths.has(route)) {
      const rolePermissions = PagePermissions[route as Pages];
      Object.entries(rolePermissions).forEach(([role, permissions]) => {
        const permissionSet = {
          create: false,
          read: false,
          update: false,
          delete: false,
          special: [] as string[],
          feature_permission: getFeaturePermission(route),
        };

        if (permissions === '*') {
          permissionSet.create = true;
          permissionSet.read = true;
          permissionSet.update = true;
          permissionSet.delete = true;
        } else if (Array.isArray(permissions)) {
          permissions.forEach((permission) => {
            const [action, specific] = permission.split(':');
            if (specific && specific !== '*') {
              permissionSet.special = [
                ...(permissionSet.special || []),
                `${action}:${specific}`,
              ];
            } else if (specific === '*') {
              switch (action) {
                case Actions.CREATE:
                  permissionSet.create = true;
                  break;
                case Actions.READ:
                  permissionSet.read = true;
                  break;
                case Actions.UPDATE:
                  permissionSet.update = true;
                  break;
                case Actions.DELETE:
                  permissionSet.delete = true;
                  break;
              }
            }
          });
        }

        formattedData[route].rolePermissions[role] = permissionSet;
      });
    }
    // Always add PUBLIC role for public routes
    if (!permissionPaths.has(route)) {
      formattedData[route].rolePermissions.PUBLIC = {
        create: false,
        read: true,
        update: true,
        delete: false,
        special: [],
        feature_permission: getFeaturePermission(route),
      };
    }
  });

  return formattedData;
}

type PermissionRow = {
  id: string;
  route: string;
  routeValue: string;
  role: CellTypes.DataGridCellBadgeParams;
  roleValue: string;
  permissions: string;
  permissionsValue: string;
  feature: string;
  featureValue: string;
};

const formatRoleLabel = (role: string): string => {
  if (role === 'VENDOR') return 'Vendor Admin';

  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

function getSpecialPermissions(route: Pages, role: RolesType): string[] {
  const rolePermissions = PagePermissions[route]?.[role];
  if (!rolePermissions || rolePermissions === '*') return [];

  return (Array.isArray(rolePermissions) ? rolePermissions : []).filter(
    (permission) => {
      const [, specific] = permission.split(':');
      return specific && specific !== '*';
    },
  );
}

const getRoleBadgeColor = (
  role: string,
): 'neutral' | 'primary' | 'purple' | 'green' | 'yellow' | 'red' => {
  switch (role) {
    case 'ADMIN':
    case 'AP_ADMIN':
      return 'red';
    case 'VENDOR':
    case 'VENDOR_STAFF':
    case 'VENDOR_OPERATIONS':
      return 'green';
    case 'COMPLIANCE':
    case 'PRODUCT_DEVELOPMENT':
    case 'BUYER':
      return 'purple';
    case 'SOCIAL_MEDIA_MANAGER':
    case 'SOCIAL_MEDIA_OPS':
    case 'INFLUENCER':
      return 'yellow';
    case 'SCHEDULER':
    case 'RECEIVER':
    case 'EXECUTIVE':
    case 'SPONSOR':
      return 'primary';
    case 'PUBLIC':
      return 'neutral';
    default:
      return 'neutral';
  }
};

const formatGridData = (
  permissionsData: PermissionDisplayData,
  selectedRoles: string[],
  selectedRoutes: string[],
) => {
  const gridData: PermissionRow[] = [];
  const routes = Object.keys(permissionsData) as Pages[];

  routes.forEach((route) => {
    // Skip if routes are filtered and this route isn't selected
    if (selectedRoutes.length > 0 && !selectedRoutes.includes(route)) {
      return;
    }

    // Process regular roles
    Object.values(Roles).forEach((role) => {
      // Skip if roles are filtered and this role isn't selected
      if (selectedRoles.length > 0 && !selectedRoles.includes(role)) {
        return;
      }

      const permissions = Object.values(Actions).reduce(
        (acc: string[], action) => {
          const rolePermissions = PagePermissions[route]?.[role as RolesType];
          if (
            rolePermissions === '*' ||
            (Array.isArray(rolePermissions) &&
              rolePermissions.includes(`${action}:*`))
          ) {
            acc.push(action);
          }
          return acc;
        },
        [],
      );

      const specialPermissions = getSpecialPermissions(
        route,
        role as RolesType,
      );

      // Only create a row if there are permissions or special permissions
      if (permissions.length > 0 || specialPermissions.length > 0) {
        const routeValue = route;

        const roleValue = formatRoleLabel(role);
        const roleBadge = {
          children: roleValue,
          color: getRoleBadgeColor(role),
          contrast: 'low' as const,
        };

        const permList = [
          ...permissions,
          ...specialPermissions.map(
            (perm) => `${perm.split(':')[0]}:${perm.split(':')[1]}`,
          ),
        ];

        const permissionsValue = permList.join(', ');

        const featurePermission =
          permissionsData[route].rolePermissions[role]?.feature_permission;
        const featureValue = featurePermission || '';

        gridData.push({
          id: `${route}-${role}`,
          route: routeValue,
          routeValue,
          role: roleBadge,
          roleValue,
          permissions: permissionsValue,
          permissionsValue,
          feature: featureValue,
          featureValue,
        });
      }
    });

    // Process PUBLIC role for routes that don't have permissions
    if (permissionsData[route].rolePermissions.PUBLIC) {
      // Skip if roles are filtered and PUBLIC isn't selected
      if (selectedRoles.length > 0 && !selectedRoles.includes('PUBLIC')) {
        return;
      }

      const routeValue = route;

      const roleValue = 'Public';
      const roleBadge = {
        children: roleValue,
        color: getRoleBadgeColor('PUBLIC'),
        contrast: 'low' as const,
      };

      const publicPermissions = permissionsData[route].rolePermissions.PUBLIC;
      const permList = Object.entries(publicPermissions)
        .filter(
          ([key, value]) => value === true && key !== 'feature_permission',
        )
        .map(([key]) => key.toUpperCase());

      const permissionsValue = permList.join(', ');

      const featurePermission =
        permissionsData[route].rolePermissions.PUBLIC?.feature_permission;
      const featureValue = featurePermission || '';

      gridData.push({
        id: `${route}-PUBLIC`,
        route: routeValue,
        routeValue,
        role: roleBadge,
        roleValue,
        permissions: permissionsValue,
        permissionsValue,
        feature: featureValue,
        featureValue,
      });
    }
  });

  return gridData;
};

export function PermissionsOverview() {
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);

  const permissionsData = useMemo(() => formatPermissionsData(), []);

  const selectedRoles = useMemo(() => urlParams.getAll('roles'), [urlParams]);
  const selectedRoutes = useMemo(() => urlParams.getAll('routes'), [urlParams]);

  const availableRoles = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(permissionsData).flatMap((info) =>
            Object.keys(info.rolePermissions),
          ),
        ),
      ),
    [permissionsData],
  );

  const availableRoutes = useMemo(() => {
    if (selectedRoles.length === 0) {
      return Object.keys(permissionsData);
    }
    return Object.entries(permissionsData)
      .filter(([, info]) =>
        selectedRoles.some((role) => info.rolePermissions[role]),
      )
      .map(([route]) => route);
  }, [permissionsData, selectedRoles]);

  const rows = useMemo(
    () => formatGridData(permissionsData, selectedRoles, selectedRoutes),
    [permissionsData, selectedRoles, selectedRoutes],
  );

  const roleFilterItems = useMemo(
    () =>
      Object.entries(UserRoleDisplay)
        .filter(([value]) => availableRoles.includes(value))
        .map(([value]) => ({
          value,
          label: formatRoleLabel(value),
        })),
    [availableRoles],
  );

  const routeFilterItems = useMemo(
    () =>
      availableRoutes.map((route) => ({
        value: route,
        label: route,
      })),
    [availableRoutes],
  );

  const updateRoles = (newRoles: string[]) => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('roles');
    newRoles.forEach((role) => newParams.append('roles', role));

    // Clear route selection if selected routes don't have the selected roles
    const validRoutes = selectedRoutes.filter((route) =>
      Object.keys(permissionsData[route]?.rolePermissions || {}).some((role) =>
        newRoles.includes(role),
      ),
    );
    newParams.delete('routes');
    validRoutes.forEach((route) => newParams.append('routes', route));

    history.push({ search: newParams.toString() });
  };

  const updateRoutes = (newRoutes: string[]) => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('routes');
    newRoutes.forEach((route) => newParams.append('routes', route));

    // Clear role selection if selected roles don't have the selected routes
    const validRoles = selectedRoles.filter((role) =>
      newRoutes.every((route) => permissionsData[route]?.rolePermissions[role]),
    );
    newParams.delete('roles');
    validRoles.forEach((role) => newParams.append('roles', role));

    history.push({ search: newParams.toString() });
  };

  return (
    <Page.Container>
      <Header.Major icon="lock/lock-close" title="Permissions Overview" />
      <Header.Minor>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row gap-4">
            <div className="flex flex-row items-center gap-2">
              <FilterSearch
                value={selectedRoles}
                items={roleFilterItems}
                label="Role"
                emptyLabel="All Roles"
                onAddValue={(value) => updateRoles([...selectedRoles, value])}
                onRemoveValue={(value) =>
                  updateRoles(selectedRoles.filter((role) => role !== value))
                }
                withSearch
              />
              {selectedRoles.length > 0 && (
                <IconButton
                  size="sm"
                  color="secondary"
                  icon={
                    <Icon name="multiple-cross/multiple-cross-cancel-default" />
                  }
                  label="Clear role filter"
                  onClick={() => updateRoles([])}
                />
              )}
            </div>
            <div className="flex flex-row items-center gap-2">
              <FilterSearch
                value={selectedRoutes}
                items={routeFilterItems}
                label="Route"
                emptyLabel="All Routes"
                onAddValue={(value) => updateRoutes([...selectedRoutes, value])}
                onRemoveValue={(value) =>
                  updateRoutes(
                    selectedRoutes.filter((route) => route !== value),
                  )
                }
                withSearch
              />
              {selectedRoutes.length > 0 && (
                <IconButton
                  size="sm"
                  color="secondary"
                  icon={
                    <Icon name="multiple-cross/multiple-cross-cancel-default" />
                  }
                  label="Clear route filter"
                  onClick={() => updateRoutes([])}
                />
              )}
            </div>
          </div>
        </div>
      </Header.Minor>
      <Page.Content>
        <DataGrid
          stateName="permissions_overview"
          sortingMode="client"
          columns={[
            {
              field: 'route',
              headerName: 'Route',
              display: 'flex',
              flex: 2,
              minWidth: 300,
              render: 'string',
              sortable: true,
            },
            {
              field: 'role',
              headerName: 'Role',
              flex: 1,
              minWidth: 150,
              render: 'badge',
              sortable: true,
            },
            {
              field: 'permissions',
              headerName: 'Permissions',
              display: 'flex',
              flex: 2,
              minWidth: 300,
              render: 'string',
              sortable: true,
            },
            {
              field: 'feature',
              headerName: 'Required Feature Permission',
              display: 'flex',
              flex: 1,
              minWidth: 150,
              render: 'string',
              sortable: true,
            },
          ]}
          rows={rows}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Page.Content>
    </Page.Container>
  );
}

export default PermissionsOverview;
